<script setup lang="ts">
	import axios from '@/lib/axios'
	import { useUsersCompanyStore } from '@/stores/usersCompany'
	import { getRoleClasses } from '@/utils/roleColors'
	import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
	import { Icon } from '@iconify/vue'
	import {
		Building,
		CircleCheck,
		CircleX,
		Mail,
		MoreHorizontal,
		MoreVertical,
		Pencil,
		Send,
		ShieldCheck,
		UserCheck,
		UserX,
		Check,
		ChevronDown,
		Download,
		Users as UsersIcon,
	} from 'lucide-vue-next'
	import { storeToRefs } from 'pinia'
	import type { AcceptableValue } from 'reka-ui'
	import { computed, ref, watch } from 'vue'
	import { useRouter } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import ModalComponent from '@/components/ModalComponent.vue'
	import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
	import { Badge } from '@/components/ui/badge'
	import { Button } from '@/components/ui/button'
	import type { UserFilters } from '@/modules/users/interfaces/userFilters'
	import {
		DropdownMenu,
		DropdownMenuContent,
		DropdownMenuItem,
		DropdownMenuLabel,
		DropdownMenuSeparator,
		DropdownMenuTrigger,
	} from '@/components/ui/dropdown-menu'
	import { Label } from '@/components/ui/label'
	import {
		Pagination,
		PaginationContent,
		PaginationEllipsis,
		PaginationFirst,
		PaginationItem,
		PaginationLast,
		PaginationNext,
		PaginationPrevious,
	} from '@/components/ui/pagination'
	import {
		Select,
		SelectContent,
		SelectGroup,
		SelectItem,
		SelectTrigger,
		SelectValue,
	} from '@/components/ui/select'
	import { Separator } from '@/components/ui/separator'
	import {
		Table,
		TableBody,
		TableCell,
		TableHead,
		TableHeader,
		TableRow,
	} from '@/components/ui/table'
	import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
	import {
		Tooltip,
		TooltipContent,
		TooltipProvider,
		TooltipTrigger,
	} from '@/components/ui/tooltip'

	import useAccount from '@/composables/useAccount'
	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useCompanies from '@/modules/companies/composables/useCompanies'
	import { Company } from '@/modules/companies/interfaces/company'
	import InviteUserForm from '@/modules/users/components/InviteUserForm.vue'
	import InviteUserMassive from '@/modules/users/components/InviteUserMassive.vue'

	import { User } from '../interfaces/user'

	type buttonList = {
		name: string
		iconName: string
		href?: string
		action?: () => void
	}

	interface Props {
		isTab?: boolean
		model?: string
		permission?: string
		users?: User[]
		buttonsList?: buttonList[]
		page?: any
		pagination?: any
		companyName?: string
		companyRequest?: Company
		internal?: boolean
		toShowUser?: { name: string }
		toEditUser?: { name: string }
		toPermissionsUser?: { name: string }
		sortBy?: string
	}

	interface Emits {
		(e: 'refetch'): void
		(event: 'update:changePage', value: string): void
		(event: 'deactivateUser', id: string): Promise<void>
		(event: 'reactivateUser', id: string): Promise<void>
	}

	const props = withDefaults(defineProps<Props>(), {
		isTab: true,
		model: '',
		permission: '',
	})

	const emits = defineEmits<Emits>()
	const router = useRouter()
	const toast = useToast()

	const store = useUsersCompanyStore()
	const { filters } = storeToRefs(store)

	const { user: userLogged } = useAuthUser()
	const { hasPermissionUser } = useHasPermissionUser()
	const {
		deactivateAccountAsync,
		reactivateAccountAsync,
		resendVerificationEmail,
	} = useAccount()
	const { companies } = useCompanies({})

	const PermissionUser = computed(() => {
		if (!userLogged.value) return null
		return {
			CreateUsersCompanies:
				!userLogged.value?.internal ? 'create-users' : 'create-internal',
			ViewUserCompanies:
				!userLogged.value?.internal ? 'view-users' : 'view-internal',
			UpdateUsersCompanies:
				!userLogged.value?.internal ? 'update-users' : 'update-internal',
			RestoreUsersCompanies:
				!userLogged.value?.internal ? 'restore-users' : 'restore-internal',
			DeleteUsersCompanies:
				!userLogged.value?.internal ? 'delete-users' : 'delete-internal',
		}
	})

	const EntityType = computed(() => {
		if (!userLogged.value) return null
		return {
			CompanyType:
				!userLogged.value?.internal ?
					'App\\Models\\Company'
				:	'App\\Models\\User',
		}
	})

	const toShowUser = props.toShowUser || {
		name: !userLogged.value?.internal ? 'users.show' : 'internal.users.show',
	}

	const modalInviteUser = ref(false)
	const actionModalInviteUser = () => {
		modalInviteUser.value = !modalInviteUser.value
	}

	const changePage = (page: number) => {
		emits('update:changePage', page.toString())
	}

	const updatePerPage = (value: AcceptableValue) => {
		store.setPage('1')
		filters.value.perPage = Number(value)
	}

	/**
	 * Una sola lista para los dos selectores (arriba junto al contador y al pie de
	 * la tabla). Si divergen, elegir un valor en uno deja al otro sin opción que
	 * empatar y se pinta vacío.
	 */
	const PER_PAGE_OPTIONS = ['10', '25', '50', '100'] as const

	/**
	 * Atajos a los huecos que deja una carga masiva de usuarios. Cada uno es una
	 * bandera booleana del backend (UserFilter::withoutSupervisor, etc.), no un
	 * `supervisor: null`: EloquentFilter ignora los valores vacíos, así que un null
	 * no filtraría nada en vez de traer a los que no tienen.
	 */
	const QUICK_FILTERS = [
		{ key: 'withoutSupervisor', label: 'Sin supervisor' },
		{ key: 'withoutDepartment', label: 'Sin departamento' },
		{ key: 'withoutAccess', label: 'Sin acceso' },
	] as const satisfies ReadonlyArray<{
		key: keyof UserFilters
		label: string
	}>

	type QuickFilterKey = (typeof QUICK_FILTERS)[number]['key']

	/* ── Selección múltiple ──────────────────────────────────────────────────────
	 *
	 * La selección NO se guarda como "lista de ids". Con 421 usuarios en páginas de
	 * 10, el caso real es "filtra por supervisor y reasigna a los 80": palomear 80
	 * casillas repartidas en 8 páginas no es viable.
	 *
	 * Por eso hay dos modos:
	 *   - explícito       → `selectedIds` tiene lo que el usuario marcó a mano.
	 *   - todo-lo-filtrado→ `selectAllMatching` en true y `excludedIds` lleva lo que
	 *                       fue desmarcando. Así "todos menos tres" son tres ids, no
	 *                       418, y no se desincroniza si alguien da de alta a otro
	 *                       usuario mientras tanto.
	 */
	const selectedIds = ref<Set<number>>(new Set())
	const selectAllMatching = ref(false)
	const excludedIds = ref<Set<number>>(new Set())

	const isSelected = (id: number) =>
		selectAllMatching.value ?
			!excludedIds.value.has(id)
		:	selectedIds.value.has(id)

	const selectedCount = computed(() =>
		selectAllMatching.value ?
			Math.max((props.pagination?.meta?.total ?? 0) - excludedIds.value.size, 0)
		:	selectedIds.value.size
	)

	const hasSelection = computed(() => selectedCount.value > 0)

	/** Última fila tocada, para poder extender la selección con shift. */
	const lastToggledId = ref<number | null>(null)

	/**
	 * `shiftKey` selecciona el rango entre la última fila tocada y esta, tomando el
	 * orden en que se ven en pantalla. Es lo que evita palomear de una en una
	 * cuando hay que mover decenas de usuarios.
	 */
	const toggleUser = (id: number, shiftKey = false) => {
		if (shiftKey && lastToggledId.value !== null) {
			const ids = pageUserIds.value
			const from = ids.indexOf(lastToggledId.value)
			const to = ids.indexOf(id)

			if (from !== -1 && to !== -1) {
				const range = ids.slice(Math.min(from, to), Math.max(from, to) + 1)
				// El rango se PONE en el mismo estado que la fila ancla, no se invierte
				// una por una: invertir dejaría el rango en damero.
				const shouldSelect = isSelected(lastToggledId.value)

				if (selectAllMatching.value) {
					const next = new Set(excludedIds.value)
					range.forEach((rid) =>
						shouldSelect ? next.delete(rid) : next.add(rid)
					)
					excludedIds.value = next
				} else {
					const next = new Set(selectedIds.value)
					range.forEach((rid) =>
						shouldSelect ? next.add(rid) : next.delete(rid)
					)
					selectedIds.value = next
				}

				lastToggledId.value = id

				return
			}
		}

		lastToggledId.value = id

		// Los Set se reasignan en vez de mutarse: Vue no rastrea `.add()`/`.delete()`
		// sobre un Set dentro de un ref, y la tabla no se re-renderizaría.
		if (selectAllMatching.value) {
			const next = new Set(excludedIds.value)
			next.has(id) ? next.delete(id) : next.add(id)
			excludedIds.value = next

			return
		}

		const next = new Set(selectedIds.value)
		next.has(id) ? next.delete(id) : next.add(id)
		selectedIds.value = next
	}

	const pageUserIds = computed(() => (props.users ?? []).map((user) => user.id))

	const allPageSelected = computed(
		() =>
			pageUserIds.value.length > 0 && pageUserIds.value.every((id) => isSelected(id))
	)

	const somePageSelected = computed(
		() => !allPageSelected.value && pageUserIds.value.some((id) => isSelected(id))
	)

	const togglePage = () => {
		const shouldSelect = !allPageSelected.value

		if (selectAllMatching.value) {
			const next = new Set(excludedIds.value)
			pageUserIds.value.forEach((id) =>
				shouldSelect ? next.delete(id) : next.add(id)
			)
			excludedIds.value = next

			return
		}

		const next = new Set(selectedIds.value)
		pageUserIds.value.forEach((id) =>
			shouldSelect ? next.add(id) : next.delete(id)
		)
		selectedIds.value = next
	}

	const clearSelection = () => {
		selectedIds.value = new Set()
		excludedIds.value = new Set()
		selectAllMatching.value = false
	}

	const selectAllMatchingFilter = () => {
		clearSelection()
		selectAllMatching.value = true
	}

	/**
	 * Payload de selección que entiende el backend (BulkUserSelectionRequest).
	 * En modo "todo lo filtrado" van los filtros, no los ids: así el servidor
	 * resuelve el conjunto contra la misma consulta que pintó la tabla.
	 */
	const selectionPayload = () =>
		selectAllMatching.value ?
			{
				mode: 'filters' as const,
				filters: { ...filters.value },
				except: [...excludedIds.value],
			}
		:	{ mode: 'ids' as const, ids: [...selectedIds.value] }

	const isExporting = ref(false)

	/* ── Acciones masivas ────────────────────────────────────────────────────────
	 *
	 * Cada una hace DOS llamadas: primero con `preview: true`, que no escribe nada
	 * y devuelve a cuántos afectaría y quiénes quedan bloqueados; con eso se pinta
	 * el modal con números reales en vez de un "¿estás seguro?". La segunda aplica.
	 *
	 * El backend es todo-o-nada: si hay bloqueados al aplicar responde 422 y no
	 * toca a nadie.
	 */
	type BulkAction = 'supervisor' | 'department' | 'access' | 'deactivate'

	/**
	 * Ojo con los nombres: el backend responde en snake_case pero el middleware
	 * ParseToCamelAfter lo convierte antes de llegar aquí. Leer `blocked_count`
	 * daba `undefined`, así que los bloqueados no se pintaban y el botón Confirmar
	 * quedaba habilitado aunque la acción fuera a ser rechazada.
	 */
	type BulkPreview = {
		affected: number
		/** Nombres de a quién se le aplica (muestra, máx. 10). */
		affectedNames?: string[]
		blockedCount: number
		blocked: { id: number; name: string; reason: string }[]
		newSeats?: number
		/**
		 * Cómo se reparte la selección por rol y qué rol necesita el supervisor de
		 * cada grupo. Un Traveler responde a un Supervisor y un Supervisor a un
		 * Travel Manager, así que una selección mixta necesita un supervisor
		 * distinto por grupo — no uno solo para todos.
		 */
		roleBreakdown?: {
			role: string
			count: number
			allowedSupervisorRoles: string[]
			names?: string[]
		}[]
	}

	const BULK_TITLES: Record<BulkAction, string> = {
		supervisor: 'Asignar supervisor',
		department: 'Cambiar departamento',
		access: 'Cambiar acceso al sistema',
		deactivate: 'Dar de baja usuarios',
	}

	const bulkAction = ref<BulkAction | null>(null)
	const bulkPayload = ref<Record<string, unknown>>({})
	const bulkPreview = ref<BulkPreview | null>(null)
	const bulkError = ref<string | null>(null)
	const bulkMenuOpen = ref(false)

	/**
	 * `bulkPayload` es un Record<string, unknown> porque cada acción manda una
	 * forma distinta. Este accesor tipa la única parte que el template necesita
	 * indexar por nombre de rol; sin él, TS no puede indexar un `unknown`.
	 */
	const supervisorByRole = computed(
		() => (bulkPayload.value.supervisorByRole ?? {}) as Record<string, number | null>
	)

	/** Cierra el menú y abre el modal. El overlay es un div propio, así que no hay
	 *  gestión de foco de por medio que pueda cerrarlo al abrirse. */
	/** Cierra el menú y abre el modal. El overlay es un div propio teleportado al
	 *  body, así que no hay gestión de foco que pueda cerrarlo al abrirse. */
	const requestBulkAction = (
		action: BulkAction,
		payload: Record<string, unknown> = {}
	) => {
		bulkMenuOpen.value = false
		openBulkAction(action, payload)
	}

	const isBulkLoading = ref(false)

	const bulkUrl = (action: BulkAction) =>
		`api/companies/${filters.value.company}/users/bulk/${action}`

	const openBulkAction = async (
		action: BulkAction,
		payload: Record<string, unknown>
	) => {
		bulkAction.value = action
		// El backend valida `present`: el selector arranca en "Sin …" (null), que es
		// un valor legítimo, no un hueco.
		bulkPayload.value =
			action === 'supervisor' ? { supervisorByRole: {}, ...payload }
			: action === 'department' ? { departmentId: null, ...payload }
			: payload
		bulkPreview.value = null
		bulkError.value = null
		isBulkLoading.value = true

		if (action === 'department') await loadBulkDepartments()
		if (action === 'supervisor') await loadSupervisorCandidates()

		try {
			const { data } = await axios.post(bulkUrl(action), {
				...selectionPayload(),
				...bulkPayload.value,
				preview: true,
			})
			bulkPreview.value = data
		} catch (error: any) {
			// El modal NO se cierra cuando falla el preview. Cerrarlo hacía que la
			// acción se viera como si no respondiera; abierto y con el motivo adentro
			// al menos dice qué pasó.
			const errors = error?.response?.data?.errors ?? {}
			bulkError.value =
				(Object.values(errors).flat()[0] as string | undefined) ??
				error?.response?.data?.message ??
				`No se pudo calcular el impacto (HTTP ${error?.response?.status ?? 'sin respuesta'}).`
		} finally {
			isBulkLoading.value = false
		}
	}

	const confirmBulkAction = async () => {
		if (!bulkAction.value) return

		isBulkLoading.value = true
		try {
			const { data } = await axios.post(bulkUrl(bulkAction.value), {
				...selectionPayload(),
				...bulkPayload.value,
			})

			toast.success(`Se actualizaron ${data.affected} usuario(s).`)
			bulkAction.value = null
			clearSelection()
			emits('refetch')
		} catch (error: any) {
			// El 422 del backend nombra a los bloqueados; mostrarlo tal cual es más
			// útil que un mensaje genérico.
			const errors = error?.response?.data?.errors ?? {}
			const first = Object.values(errors).flat()[0] as string | undefined
			toast.error(first ?? 'No se pudo aplicar la acción.')
		} finally {
			isBulkLoading.value = false
		}
	}

	const cancelBulkAction = () => {
		bulkAction.value = null
		bulkPreview.value = null
	}

	// Candidatos a supervisor y departamentos disponibles para los selectores del
	// modal. Se piden sólo cuando hace falta, no al montar la lista.
	/**
	 * Candidatos a supervisor. Se piden directo y no con useUsersSearch porque ese
	 * composable guarda en un store GLOBAL compartido con el filtro "Supervisado
	 * por": cualquier búsqueda desde ahí pisaría la lista del modal. Mismo motivo
	 * que con los departamentos.
	 */
	const supervisorCandidates = ref<User[]>([])
	const loadingSupervisors = ref(false)

	/** Candidatos que sirven para un grupo, según el rol que ese grupo exige. */
	const candidatesForRoles = (allowedRoles: string[]) =>
		supervisorCandidates.value.filter((candidate) =>
			(candidate.roles ?? []).some((role) => allowedRoles.includes(role))
		)

	const loadSupervisorCandidates = async () => {
		if (
			loadingSupervisors.value ||
			supervisorCandidates.value.length ||
			!filters.value.company
		)
			return

		loadingSupervisors.value = true
		try {
			const { data } = await axios.get<User[]>('/api/users/search', {
				params: {
					company: filters.value.company,
					// Los roles POR ENCIMA de Traveler: Master, Travel Manager y
					// Supervisor. El backend resuelve la jerarquía.
					supervisors: ['Traveler'],
				},
			})
			supervisorCandidates.value = data ?? []
		} catch {
			bulkError.value = 'No se pudieron cargar los supervisores.'
		} finally {
			loadingSupervisors.value = false
		}
	}

	/**
	 * Departamentos del selector. Se piden directo y no con useCompanyDepartment
	 * porque ese composable arma su queryKey con el companyId evaluado al montar
	 * (si `filters.company` todavía no resolvió, cachea `undefined` y no reintenta)
	 * y además lee de un store global que otras pantallas reapuntan a otra empresa.
	 * Aquí sólo se necesita una lista, al abrir el modal.
	 */
	const bulkDepartments = ref<{ id: number; name: string }[]>([])

	const loadingDepartments = ref(false)

	const loadBulkDepartments = async () => {
		// `loadingDepartments` además del length: la petición tarda, y sin el candado
		// varias llamadas seguidas disparan varias peticiones antes de que la primera
		// resuelva.
		if (
			loadingDepartments.value ||
			bulkDepartments.value.length ||
			!filters.value.company
		)
			return

		loadingDepartments.value = true

		try {
			const { data } = await axios.get(
				`api/companies/${filters.value.company}/departments`,
				{ params: { perPage: 100 } }
			)
			bulkDepartments.value = data.data ?? data
		} catch {
			bulkError.value = 'No se pudieron cargar los departamentos.'
		} finally {
			loadingDepartments.value = false
		}
	}

	/**
	 * Recalcula el preview tras elegir un valor. Se dispara desde el `@change` del
	 * select y NO desde un watch sobre `bulkPayload`: ese getter devolvía un array
	 * nuevo en cada evaluación, Vue lo comparaba por referencia y se disparaba
	 * siempre — y como `openBulkAction` reasigna `bulkPayload`, se realimentaba en
	 * bucle infinito de peticiones.
	 */
	/**
	 * Guarda el supervisor elegido para un rol y recalcula. El `<select>` devuelve
	 * string siempre, así que "" (la opción "Sin supervisor") se normaliza a null,
	 * que es lo que el backend entiende como "quitar el supervisor".
	 */
	const setSupervisorForRole = (role: string, rawValue: string) => {
		bulkPayload.value = {
			...bulkPayload.value,
			supervisorByRole: {
				...supervisorByRole.value,
				[role]: rawValue === '' || rawValue === 'null' ? null : Number(rawValue),
			},
		}

		refreshBulkPreview()
	}

	const refreshBulkPreview = async () => {
		if (!bulkAction.value) return

		bulkPreview.value = null
		bulkError.value = null
		isBulkLoading.value = true

		try {
			const { data } = await axios.post(bulkUrl(bulkAction.value), {
				...selectionPayload(),
				...bulkPayload.value,
				preview: true,
			})
			bulkPreview.value = data
		} catch (error: any) {
			const errors = error?.response?.data?.errors ?? {}
			bulkError.value =
				(Object.values(errors).flat()[0] as string | undefined) ??
				error?.response?.data?.message ??
				`No se pudo calcular el impacto (HTTP ${error?.response?.status ?? 'sin respuesta'}).`
		} finally {
			isBulkLoading.value = false
		}
	}


	const exportSelection = async () => {
		if (isExporting.value) return

		isExporting.value = true
		try {
			const { data } = await axios.post(
				`api/companies/${filters.value.company}/users/bulk/export`,
				selectionPayload(),
				{ responseType: 'blob' }
			)

			// El backend manda el CSV como stream; se ancla a un <a> temporal porque
			// no hay forma de disparar la descarga de un blob sin pasar por el DOM.
			const url = URL.createObjectURL(data)
			const link = document.createElement('a')
			link.href = url
			link.download = `usuarios-${new Date().toISOString().slice(0, 10)}.csv`
			link.click()
			URL.revokeObjectURL(url)
		} catch {
			toast.error('No se pudo exportar la selección.')
		} finally {
			isExporting.value = false
		}
	}

	/**
	 * Cambiar de filtro cambia el universo seleccionado. Sin esto, "todos los
	 * filtrados" seguiría vigente contra OTRO conjunto de usuarios — se actuaría
	 * sobre gente que el usuario nunca vio. Paginar no limpia: ahí el universo es
	 * el mismo y perder la selección al cambiar de página es justo el problema.
	 */
	watch(
		() => [
			filters.value.search,
			filters.value.status,
			filters.value.role,
			filters.value.group,
			filters.value.department,
			filters.value.supervisor,
			filters.value.withoutSupervisor,
			filters.value.withoutDepartment,
			filters.value.withoutAccess,
		],
		clearSelection,
		{ deep: true }
	)

	/**
	 * Alterna un filtro rápido. Al apagarlo se borra la llave en vez de mandar
	 * `false`, para no ensuciar el query string con banderas inertes.
	 *
	 * "Sin supervisor" también limpia el supervisor elegido en el filtro
	 * "Supervisado por": juntos no devuelven nada, porque nadie tiene un supervisor
	 * concreto y a la vez ninguno.
	 */
	const toggleQuickFilter = (key: QuickFilterKey) => {
		store.setPage('1')
		const next = filters.value[key] ? undefined : true

		if (key === 'withoutSupervisor' && next) {
			store.setSupervisorUser(undefined)
		}

		filters.value[key] = next
	}

	/**
	 * El grupo que se autocrea por empresa se llama "Default" en base de datos, y a
	 * los usuarios no les dice nada. Se muestra como "Principal" y sólo en ese caso
	 * se explica con tooltip qué significa. Es un alias de presentación: el nombre
	 * real no se toca, así que los grupos que la empresa sí nombró salen tal cual.
	 */
	const DEFAULT_GROUP_NAME = 'Default'
	const DEFAULT_GROUP_LABEL = 'Principal'
	const DEFAULT_GROUP_HINT = 'Política general de la empresa'

	const isDefaultGroup = (groupName?: string | null) =>
		groupName?.trim().toLowerCase() === DEFAULT_GROUP_NAME.toLowerCase()

	const groupLabel = (groupName?: string | null) =>
		isDefaultGroup(groupName) ? DEFAULT_GROUP_LABEL : groupName || 'Sin grupo'

	const currentStatus = computed(() => filters.value.status || 'all')
	const updateStatus = (value: string | number) => {
		const status = String(value)
		store.setPage('1')
		filters.value.status = status === 'all' ? null : status
	}

	const getInitials = (name?: string, lastname?: string) =>
		`${name?.[0] ?? ''}${lastname?.[0] ?? ''}`.toUpperCase()

	const handleSendVerifyEmail = async (id: string) => {
		await resendVerificationEmail({
			params: { id },
			config: {
				raiseToast: true,
				onSuccessToast: 'Se ha enviado un correo de verificación al usuario.',
			},
		})
	}

	const handleDeactivateUser = async (id: string) => {
		await deactivateAccountAsync({
			params: { id: Number(id) },
			config: {
				raiseToast: true,
				onSuccessToast: 'El usuario ha sido desactivado correctamente.',
			},
		})
		emits('refetch')
	}

	const handleReactiveUser = async (id: string) => {
		await reactivateAccountAsync({
			params: { id: Number(id) },
			config: {
				raiseToast: true,
				onSuccessToast: 'El usuario ha sido reactivado correctamente.',
				onErrorToast: 'No se ha podido reactivar el usuario.',
			},
		})
		emits('refetch')
	}

	defineExpose({ actionModalInviteUser })
</script>

<template>
	<div class="w-full space-y-4">
		<!-- Slot filters -->
		<div>
			<slot name="filters" />
		</div>

		<!-- Invitation Modal -->
		<ModalComponent
			:model-value="modalInviteUser"
			@update:model-value="actionModalInviteUser"
		>
			<template #modalBody>
				<div class="overflow-hidden rounded-lg">
					<!-- Navy header -->
					<div class="bg-[#021F58] px-6 py-5 flex items-center justify-between">
						<div class="flex items-center gap-3">
							<div class="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
								<span class="material-symbols-outlined text-white text-xl">send</span>
							</div>
							<div>
								<p class="text-xs text-white/60 font-medium uppercase tracking-widest">Usuarios</p>
								<h2 class="text-white font-bold text-lg leading-tight">Enviar invitación</h2>
							</div>
						</div>
						<button
							class="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-colors"
							@click="actionModalInviteUser"
						>
							<span class="material-symbols-outlined text-white text-lg">close</span>
						</button>
					</div>

					<TabGroup>
						<!-- Custom tab bar -->
						<TabList as="div" class="flex border-b border-gray-100 bg-gray-50">
							<Tab
								v-for="tab in ['Invitación Individual', 'Invitación a Varios Usuarios']"
								:key="tab"
								v-slot="{ selected }"
								as="template"
							>
								<button
									class="flex-1 px-4 py-3 text-sm font-medium transition-colors focus:outline-none"
									:class="selected
										? 'border-b-2 border-[#021F58] text-[#021F58] bg-white'
										: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-b-2 border-transparent'"
								>
									{{ tab }}
								</button>
							</Tab>
						</TabList>

						<TabPanels class="px-6 py-5">
							<TabPanel>
								<InviteUserForm
									:companies="companies"
									@action-modal-invite-user="actionModalInviteUser"
								/>
							</TabPanel>
							<TabPanel>
								<InviteUserMassive
									:companies="companies"
									@action-modal-invite-user="actionModalInviteUser"
								/>
							</TabPanel>
						</TabPanels>
					</TabGroup>
				</div>
			</template>
		</ModalComponent>

		<!-- Confirmación de acción masiva -->
		<Teleport to="body">
			<div
				v-if="bulkAction"
				data-bulk-modal
				class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
			>
				<div class="w-full max-w-md rounded-xl bg-white shadow-xl">
					<div class="p-6 space-y-4">
						<h3 class="text-lg font-semibold">
							{{ bulkAction ? BULK_TITLES[bulkAction] : '' }}
						</h3>

						<!-- Un selector POR ROL, no uno solo: la jerarquía es una escalera
						     (Traveler → Supervisor → Travel Manager → Master), así que una
						     selección mixta necesita un encargado distinto por grupo. -->
						<div
							v-if="bulkAction === 'supervisor'"
							class="space-y-3"
						>
							<template
								v-for="group in bulkPreview?.roleBreakdown ?? []"
								:key="group.role"
							>
								<div v-if="group.allowedSupervisorRoles.length">
									<Label class="text-sm">
										{{ group.count }}
										{{ group.count === 1 ? 'usuario' : 'usuarios' }} con rol
										<strong>{{ group.role }}</strong>
										— queda a cargo de un
										{{ group.allowedSupervisorRoles.join(' o ') }}
									</Label>
									<p class="mt-0.5 text-xs text-muted-foreground">
										{{ (group.names ?? []).join(', ')
										}}<template v-if="group.count > (group.names?.length ?? 0)">
											y {{ group.count - (group.names?.length ?? 0) }} más
										</template>
									</p>
									<select
										:value="supervisorByRole[group.role] ?? null"
										class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
										@change="
											(e: Event) =>
												setSupervisorForRole(
													group.role,
													(e.target as HTMLSelectElement).value
												)
										"
									>
										<option :value="null">Sin supervisor</option>
										<option
											v-for="candidate in candidatesForRoles(
												group.allowedSupervisorRoles
											)"
											:key="candidate.id"
											:value="candidate.id"
										>
											{{ candidate.name }} {{ candidate.lastname }}
										</option>
									</select>
									<p
										v-if="!candidatesForRoles(group.allowedSupervisorRoles).length"
										class="mt-1 text-xs text-destructive"
									>
										No hay nadie con rol
										{{ group.allowedSupervisorRoles.join(' o ') }} en esta
										empresa.
									</p>
								</div>

								<p
									v-else
									class="text-xs text-muted-foreground"
								>
									{{ group.count }}
									{{ group.count === 1 ? 'usuario' : 'usuarios' }} con rol
									<strong>{{ group.role }}</strong> no llevan supervisor por su
									rol; se quedan como están.
								</p>
							</template>
						</div>

						<div v-if="bulkAction === 'department'">
							<Label class="text-sm">Nuevo departamento</Label>
							<select
								v-model="bulkPayload.departmentId"
								@change="refreshBulkPreview"
								class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
							>
								<option :value="null">Sin departamento</option>
								<option
									v-for="department in bulkDepartments ?? []"
									:key="department.id"
									:value="department.id"
								>
									{{ department.name }}
								</option>
							</select>
						</div>

						<p
							v-if="isBulkLoading && !bulkPreview"
							class="text-sm text-muted-foreground"
						>
							Calculando a cuántos afecta…
						</p>

						<p
							v-if="bulkError"
							class="rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
						>
							{{ bulkError }}
						</p>

						<div
							v-if="bulkPreview"
							class="rounded-lg bg-muted/50 p-3 text-sm space-y-1.5"
						>
							<p>
								Se actualizarán
								<strong>{{ bulkPreview.affected }}</strong> usuario(s).
							</p>
							<p
								v-if="bulkPreview.affectedNames?.length"
								class="text-xs text-muted-foreground"
							>
								{{ bulkPreview.affectedNames.join(', ')
								}}<template
									v-if="bulkPreview.affected > bulkPreview.affectedNames.length"
								>
									y
									{{ bulkPreview.affected - bulkPreview.affectedNames.length }}
									más
								</template>
							</p>
							<p
								v-if="bulkPreview.newSeats"
								class="text-xs text-muted-foreground"
							>
								{{ bulkPreview.newSeats }} consumirán un lugar nuevo de tu plan.
							</p>
							<div
								v-if="bulkPreview.blockedCount"
								class="text-xs text-destructive"
							>
								<p class="font-medium">
									{{ bulkPreview.blockedCount }} no se pueden modificar:
								</p>
								<ul class="mt-1 space-y-0.5 list-disc list-inside">
									<li
										v-for="entry in bulkPreview.blocked"
										:key="entry.id"
									>
										{{ entry.name }} — {{ entry.reason }}
									</li>
								</ul>
								<p class="mt-1.5 font-medium">
									Mientras haya bloqueados no se aplica ningún cambio.
								</p>
							</div>
						</div>

						<div class="flex justify-end gap-2 pt-1">
							<Button
								variant="ghost"
								size="sm"
								:disabled="isBulkLoading"
								@click="cancelBulkAction"
								>Cancelar</Button
							>
							<Button
								size="sm"
								:variant="bulkAction === 'deactivate' ? 'destructive' : 'default'"
								:disabled="
									isBulkLoading ||
									!bulkPreview ||
									bulkPreview.affected === 0 ||
									bulkPreview.blockedCount > 0
								"
								@click="confirmBulkAction"
							>
								{{ isBulkLoading ? 'Aplicando…' : 'Confirmar' }}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Status Tabs -->
		<Tabs
			:model-value="currentStatus"
			@update:model-value="updateStatus"
		>
			<TabsList class="h-11 p-1.5 bg-muted/50 border rounded-xl gap-1">
				<TabsTrigger
					value="all"
					class="h-8 px-5 rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground"
				>
					<span class="flex items-center gap-1.5">
						<UsersIcon class="w-3.5 h-3.5" />
						Todos
					</span>
				</TabsTrigger>
				<TabsTrigger
					value="active"
					class="h-8 px-5 rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-emerald-500 data-[state=active]:text-white data-[state=active]:shadow-sm"
				>
					<span class="flex items-center gap-1.5">
						<CircleCheck class="w-3.5 h-3.5" />
						Activos
					</span>
				</TabsTrigger>
				<TabsTrigger
					value="inactive"
					class="h-8 px-5 rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-rose-500 data-[state=active]:text-white data-[state=active]:shadow-sm"
				>
					<span class="flex items-center gap-1.5">
						<CircleX class="w-3.5 h-3.5" />
						Inactivos
					</span>
				</TabsTrigger>
			</TabsList>
		</Tabs>

		<!-- Filtros rápidos -->
		<!-- Son los huecos típicos de una carga masiva: los usuarios se dan de alta en
		     lote y quedan sin supervisor, sin departamento o sin acceso. Llegar a eso
		     por el panel de Filtros son varios clics; aquí es uno. -->
		<div class="flex flex-wrap items-center gap-2">
			<span class="text-xs text-muted-foreground mr-1">Filtros rápidos:</span>
			<Button
				v-for="quickFilter in QUICK_FILTERS"
				:key="quickFilter.key"
				size="sm"
				:variant="filters[quickFilter.key] ? 'default' : 'outline'"
				class="h-7 rounded-full px-3 text-xs font-medium"
				:aria-pressed="!!filters[quickFilter.key]"
				@click="toggleQuickFilter(quickFilter.key)"
			>
				{{ quickFilter.label }}
				<CircleX
					v-if="filters[quickFilter.key]"
					class="ml-1.5 w-3 h-3"
				/>
			</Button>
		</div>

		<!-- Barra de acciones masivas -->
		<div
			v-if="hasSelection"
			class="flex flex-wrap items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5"
		>
			<span class="text-sm font-semibold text-foreground">
				Seleccionados: {{ selectedCount }}
			</span>
			<span class="text-xs text-muted-foreground hidden sm:inline">
				Shift + clic selecciona un rango
			</span>

			<!-- El puente entre "marqué esta página" y "quiero los 421": sin esto, la
			     única forma de actuar sobre todo el filtro es paginar marcando. -->
			<button
				v-if="!selectAllMatching && allPageSelected && pagination?.meta?.total > pageUserIds.length"
				class="text-xs font-medium text-primary underline underline-offset-2"
				@click="selectAllMatchingFilter"
			>
				Seleccionar los {{ pagination.meta.total }} que coinciden con el filtro
			</button>
			<span
				v-else-if="selectAllMatching"
				class="text-xs text-muted-foreground"
			>
				Todos los que coinciden con el filtro actual
			</span>

			<div class="ml-auto flex items-center gap-2">
				<!-- Supervisor y departamento piden un valor, así que el selector vive
				     en el modal. Acceso y baja no piden nada: van directo al preview. -->
				<DropdownMenu v-model:open="bulkMenuOpen">
					<DropdownMenuTrigger as-child>
						<Button
							size="sm"
							variant="outline"
							class="h-8 gap-1.5 text-xs"
						>
							Acciones
							<ChevronDown class="h-3.5 w-3.5" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuLabel class="text-xs"
							>{{ selectedCount }} seleccionados</DropdownMenuLabel
						>
						<DropdownMenuSeparator />
						<DropdownMenuItem @click="requestBulkAction('supervisor')">
							<UserCheck class="mr-2 h-3.5 w-3.5" />
							Asignar supervisor
						</DropdownMenuItem>
						<DropdownMenuItem @click="requestBulkAction('department')">
							<Building class="mr-2 h-3.5 w-3.5" />
							Cambiar departamento
						</DropdownMenuItem>
						<DropdownMenuItem
							@click="requestBulkAction('access', { haveAccess: true })"
						>
							<CircleCheck class="mr-2 h-3.5 w-3.5" />
							Habilitar acceso
						</DropdownMenuItem>
						<DropdownMenuItem
							@click="requestBulkAction('access', { haveAccess: false })"
						>
							<CircleX class="mr-2 h-3.5 w-3.5" />
							Quitar acceso
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							class="text-destructive focus:text-destructive"
							@click="requestBulkAction('deactivate')"
						>
							<UserX class="mr-2 h-3.5 w-3.5" />
							Dar de baja
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>

				<Button
					size="sm"
					variant="outline"
					class="h-8 gap-1.5 text-xs"
					:disabled="isExporting"
					@click="exportSelection"
				>
					<Download class="h-3.5 w-3.5" />
					{{ isExporting ? 'Exportando…' : 'Exportar' }}
				</Button>
				<Button
					size="sm"
					variant="ghost"
					class="h-8 text-xs"
					@click="clearSelection"
				>
					Limpiar
				</Button>
			</div>
		</div>

		<!-- Results summary -->
		<div
			v-if="users?.length && pagination?.meta?.total"
			class="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-muted-foreground"
		>
			<UsersIcon class="w-4 h-4" />
			<span>
				<strong class="text-foreground font-semibold">{{
					pagination.meta.total
				}}</strong>
				usuarios
			</span>
			<Separator
				orientation="vertical"
				class="h-4"
			/>
			<span>Mostrando {{ pagination.meta.from }}–{{ pagination.meta.to }}</span>

			<!-- El selector de filas ya existía, pero sólo al pie de la tabla: con 421
			     usuarios había que recorrer toda la lista para llegar a él. Se repite
			     aquí, junto al contador, que es donde se lee cuántos se están viendo. -->
			<Separator
				orientation="vertical"
				class="h-4"
			/>
			<div class="flex items-center gap-2">
				<Label
					for="users-per-page-top"
					class="text-xs text-muted-foreground"
					>Filas:</Label
				>
				<Select
					:model-value="filters.perPage?.toString()"
					@update:model-value="updatePerPage"
				>
					<SelectTrigger
						id="users-per-page-top"
						class="h-8 w-[70px] text-xs"
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							<SelectItem
								v-for="option in PER_PAGE_OPTIONS"
								:key="option"
								:value="option"
								>{{ option }}</SelectItem
							>
						</SelectGroup>
					</SelectContent>
				</Select>
			</div>
		</div>

		<!-- Desktop Table -->
		<div
			class="hidden lg:block rounded-xl border bg-card shadow-sm overflow-hidden"
		>
			<Table>
				<TableHeader>
					<TableRow class="bg-muted/40 hover:bg-muted/40">
						<TableHead class="w-[280px] py-3 pl-4 font-semibold text-foreground/80">
							<span class="inline-flex items-center gap-3">
								<input
									type="checkbox"
									class="h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary"
									:checked="allPageSelected"
									:indeterminate="somePageSelected"
									:aria-label="
										allPageSelected ?
											'Deseleccionar los de esta página'
										:	'Seleccionar los de esta página'
									"
									@change="togglePage"
								/>
								Usuario
							</span>
						</TableHead>
						<TableHead class="py-3 font-semibold text-foreground/80"
							>Roles</TableHead
						>
						<TableHead class="py-3 font-semibold text-foreground/80"
							>Organización</TableHead
						>
						<TableHead class="py-3 text-center font-semibold text-foreground/80"
							>Estado</TableHead
						>
						<TableHead
							class="py-3 text-right font-semibold text-foreground/80 pr-4"
							>Acciones</TableHead
						>
					</TableRow>
				</TableHeader>
				<TableBody>
					<template v-if="users?.length">
						<TableRow
							v-for="user in users"
							:key="user.id"
							class="group transition-colors"
							:class="[
								user.deletedAt ? 'opacity-60' : 'hover:bg-muted/30',
								isSelected(user.id) ? 'bg-primary/5' : '',
							]"
						>
							<!-- User info -->
							<TableCell class="py-3 pl-4">
								<div class="flex items-center gap-3">
									<!-- El avatar ES el control de selección: al pasar el mouse
									     (o si ya está seleccionado) se cambia por un checkbox.
									     Así no hace falta una columna extra sólo para eso. -->
									<button
										type="button"
										class="relative h-9 w-9 shrink-0"
										:aria-pressed="isSelected(user.id)"
										:aria-label="`Seleccionar a ${user.name} ${user.lastname}`"
										@click="(e: MouseEvent) => toggleUser(user.id, e.shiftKey)"
									>
										<Avatar
											class="h-9 w-9 border shadow-sm transition-opacity"
											:class="
												isSelected(user.id) ?
													'opacity-0'
												:	'opacity-100 group-hover:opacity-0'
											"
										>
											<AvatarImage :src="user.avatar" />
											<AvatarFallback
												class="text-xs font-bold bg-primary/10 text-primary"
											>
												{{ getInitials(user.name, user.lastname) }}
											</AvatarFallback>
										</Avatar>
										<span
											class="absolute inset-0 flex items-center justify-center rounded-full border transition-opacity"
											:class="
												isSelected(user.id) ?
													'opacity-100 border-primary bg-primary'
												:	'opacity-0 group-hover:opacity-100 border-gray-300 bg-white'
											"
										>
											<Check
												v-if="isSelected(user.id)"
												class="h-4 w-4 text-white"
											/>
										</span>
									</button>
									<div class="min-w-0">
										<RouterLink
											v-if="
												hasPermissionUser(
													PermissionUser?.ViewUserCompanies ?? '',
													EntityType?.CompanyType ?? ''
												) && !user.deletedAt
											"
											:to="{ name: toShowUser.name, params: { id: user.id } }"
											class="font-semibold text-foreground hover:text-primary transition-colors block truncate leading-tight"
										>
											{{ user.name }} {{ user.lastname }}
										</RouterLink>
										<span
											v-else
											class="font-semibold text-foreground block truncate leading-tight"
										>
											{{ user.name }} {{ user.lastname }}
										</span>
										<div class="flex items-center gap-1 mt-0.5">
											<Mail class="w-3 h-3 text-muted-foreground shrink-0" />
											<span class="text-xs text-muted-foreground truncate">
												{{ user.email || 'Sin acceso al sistema' }}
											</span>
											<TooltipProvider
												v-if="user.email && !user.emailVerifiedAt"
											>
												<Tooltip>
													<TooltipTrigger>
														<span
															class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-600 text-[9px] font-bold ml-0.5"
															>!</span
														>
													</TooltipTrigger>
													<TooltipContent>Correo no verificado</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										</div>
									</div>
								</div>
							</TableCell>

							<!-- Roles -->
							<TableCell class="py-3">
								<div class="flex flex-wrap gap-1 max-w-[200px]">
									<Badge
										v-for="role in user.roles"
										:key="role"
										variant="secondary"
										:class="[
											'text-[10px] font-bold uppercase tracking-wide px-1.5 py-0 h-5',
											getRoleClasses(role).class,
										]"
									>
										{{ getRoleClasses(role).label || role }}
									</Badge>
									<span
										v-if="!user.roles?.length"
										class="text-xs text-muted-foreground italic"
										>Sin roles</span
									>
								</div>
							</TableCell>

							<!-- Org -->
							<!-- Las tres líneas tenían el mismo peso y sólo un icono para
							     distinguirlas, así que "Default / Sin departamento / WendyFrias"
							     se leía como un bloque indistinto. El grupo sube a badge (es la
							     política que aplica) y los otros dos quedan como pares
							     etiqueta-valor, que sí se escanean. -->
							<TableCell class="py-3">
								<div class="space-y-1.5">
									<TooltipProvider :delay-duration="200">
										<Tooltip :disabled="!isDefaultGroup(user.groupName)">
											<TooltipTrigger as-child>
												<Badge
													variant="secondary"
													class="gap-1.5 font-medium max-w-[170px]"
												>
													<ShieldCheck class="w-3 h-3 shrink-0 text-primary/70" />
													<span class="truncate">{{
														groupLabel(user.groupName)
													}}</span>
												</Badge>
											</TooltipTrigger>
											<TooltipContent v-if="isDefaultGroup(user.groupName)">
												{{ DEFAULT_GROUP_HINT }}
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>

									<div
										class="flex items-baseline gap-1.5 text-xs text-muted-foreground"
									>
										<Building
											class="w-3.5 h-3.5 shrink-0 self-center opacity-70"
										/>
										<span class="truncate max-w-[150px]">{{
											user.departmentName || 'Sin departamento'
										}}</span>
									</div>

									<!-- Siempre visible, aunque no haya supervisor: cuando esta línea
									     aparecía sólo a veces, unas filas medían dos renglones y
									     otras tres, y la tabla se veía desalineada. -->
									<div
										class="flex items-baseline gap-1.5 text-xs text-muted-foreground"
									>
										<UserCheck
											class="w-3.5 h-3.5 shrink-0 self-center opacity-70"
										/>
										<span
											v-if="user.supervisorName"
											class="truncate max-w-[150px] text-foreground/80"
											>{{ user.supervisorName }}</span
										>
										<span
											v-else
											class="italic opacity-70"
											>Sin supervisor</span
										>
									</div>
								</div>
							</TableCell>

							<!-- Status -->
							<TableCell class="py-3 text-center">
								<Badge
									:class="
										user.deletedAt ?
											'bg-rose-50 text-rose-600 border-rose-200'
										:	'bg-emerald-50 text-emerald-700 border-emerald-200'
									"
									variant="outline"
									class="rounded-full text-[10px] font-semibold px-2"
								>
									<span
										class="inline-block w-1.5 h-1.5 rounded-full mr-1.5"
										:class="user.deletedAt ? 'bg-rose-400' : 'bg-emerald-500'"
									/>
									{{ user.deletedAt ? 'Inactivo' : 'Activo' }}
								</Badge>
							</TableCell>

							<!-- Actions -->
							<TableCell class="py-3 text-right pr-4">
								<DropdownMenu v-if="user.isOwned || user.deletedAt">
									<DropdownMenuTrigger as-child>
										<Button
											variant="ghost"
											size="icon"
											class="h-8 w-8 transition-colors"
										>
											<MoreHorizontal class="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										align="end"
										class="w-48"
									>
										<DropdownMenuLabel
											class="text-xs font-semibold text-muted-foreground"
											>Acciones</DropdownMenuLabel
										>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											v-if="
												!user.deletedAt &&
												hasPermissionUser(
													PermissionUser?.UpdateUsersCompanies ?? '',
													EntityType?.CompanyType ?? ''
												)
											"
											@click="
												router.push({
													name: props.toEditUser?.name || 'users.edit',
													params: { id: user.id },
												})
											"
										>
											<Pencil class="w-3.5 h-3.5 mr-2" /> Editar
										</DropdownMenuItem>
										<DropdownMenuItem
											v-if="!user.deletedAt"
											@click="router.push({ name: 'users.permissions', params: { id: user.id } })"
										>
											<ShieldCheck class="w-3.5 h-3.5 mr-2" /> Permisos
										</DropdownMenuItem>
										<DropdownMenuItem
											v-if="
												user.email && !user.emailVerifiedAt && !user.deletedAt
											"
											@click="handleSendVerifyEmail(user.id.toString())"
										>
											<Send class="w-3.5 h-3.5 mr-2" /> Reenviar verificación
										</DropdownMenuItem>
										<DropdownMenuSeparator
											v-if="
												hasPermissionUser(
													PermissionUser?.DeleteUsersCompanies ?? '',
													EntityType?.CompanyType ?? ''
												) ||
												hasPermissionUser(
													PermissionUser?.RestoreUsersCompanies ?? '',
													EntityType?.CompanyType ?? ''
												)
											"
										/>
										<DropdownMenuItem
											v-if="
												!user.deletedAt &&
												user.id !== userLogged?.id &&
												hasPermissionUser(
													PermissionUser?.DeleteUsersCompanies ?? '',
													EntityType?.CompanyType ?? ''
												)
											"
											class="text-destructive focus:text-destructive"
											@click="handleDeactivateUser(user.id.toString())"
										>
											<UserX class="w-3.5 h-3.5 mr-2" /> Desactivar
										</DropdownMenuItem>
										<DropdownMenuItem
											v-if="
												user.deletedAt &&
												hasPermissionUser(
													PermissionUser?.RestoreUsersCompanies ?? '',
													EntityType?.CompanyType ?? ''
												)
											"
											class="text-emerald-600 focus:text-emerald-600"
											@click="handleReactiveUser(user.id.toString())"
										>
											<UserCheck class="w-3.5 h-3.5 mr-2" /> Activar
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</TableCell>
						</TableRow>
					</template>

					<TableRow v-else>
						<TableCell
							colspan="5"
							class="h-40 text-center"
						>
							<div
								class="flex flex-col items-center gap-2 text-muted-foreground"
							>
								<UsersIcon class="w-8 h-8 opacity-30" />
								<p class="text-sm">No se encontraron usuarios</p>
							</div>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</div>

		<!-- Mobile Cards -->
		<div class="lg:hidden space-y-2">
			<div
				v-if="!users?.length"
				class="flex flex-col items-center gap-2 py-12 text-muted-foreground"
			>
				<UsersIcon class="w-8 h-8 opacity-30" />
				<p class="text-sm">No se encontraron usuarios</p>
			</div>
			<div
				v-for="user in users"
				:key="user.id"
				class="bg-card border rounded-xl p-4 shadow-sm"
				:class="[
					user.deletedAt ? 'opacity-60' : '',
					isSelected(user.id) ? 'ring-2 ring-primary/40' : '',
				]"
			>
				<div class="flex items-start gap-3">
					<!-- La tabla con checkboxes es `hidden lg:block`: sin esto, abajo de
					     1024px no hay forma de seleccionar y las acciones masivas
					     simplemente no existen en esa vista. -->
					<input
						type="checkbox"
						class="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 accent-primary"
						:checked="isSelected(user.id)"
						:aria-label="`Seleccionar a ${user.name} ${user.lastname}`"
						@click="(e: MouseEvent) => toggleUser(user.id, e.shiftKey)"
					/>
					<Avatar class="h-11 w-11 border shadow-sm shrink-0">
						<AvatarImage :src="user.avatar" />
						<AvatarFallback
							class="text-xs font-bold bg-primary/10 text-primary"
						>
							{{ getInitials(user.name, user.lastname) }}
						</AvatarFallback>
					</Avatar>
					<div class="flex-1 min-w-0">
						<div class="flex items-start justify-between gap-2">
							<div class="min-w-0">
								<p class="font-semibold text-sm text-foreground truncate">
									{{ user.name }} {{ user.lastname }}
								</p>
								<p class="text-xs text-muted-foreground truncate">
									{{ user.email || 'Sin correo' }}
								</p>
							</div>
							<Badge
								:class="
									user.deletedAt ?
										'bg-rose-50 text-rose-600 border-rose-200'
									:	'bg-emerald-50 text-emerald-700 border-emerald-200'
								"
								variant="outline"
								class="rounded-full text-[10px] shrink-0"
							>
								{{ user.deletedAt ? 'Inactivo' : 'Activo' }}
							</Badge>
						</div>
					</div>
				</div>

				<div class="grid grid-cols-2 gap-2 mt-3">
					<div class="bg-muted/40 rounded-lg p-2">
						<p
							class="text-[10px] text-muted-foreground uppercase font-bold tracking-wide mb-0.5"
						>
							Grupo
						</p>
						<p class="text-xs font-medium truncate">
							{{ groupLabel(user.groupName) }}
						</p>
						<!-- En la tarjeta no hay hover, así que la aclaración va como texto. -->
						<p
							v-if="isDefaultGroup(user.groupName)"
							class="text-[10px] text-muted-foreground leading-tight mt-0.5"
						>
							{{ DEFAULT_GROUP_HINT }}
						</p>
					</div>
					<div class="bg-muted/40 rounded-lg p-2">
						<p
							class="text-[10px] text-muted-foreground uppercase font-bold tracking-wide mb-0.5"
						>
							Depto.
						</p>
						<p class="text-xs font-medium truncate">
							{{ user.departmentName || 'N/A' }}
						</p>
					</div>
					<div class="bg-muted/40 rounded-lg p-2 col-span-2">
						<p
							class="text-[10px] text-muted-foreground uppercase font-bold tracking-wide mb-0.5"
						>
							Supervisor
						</p>
						<p
							class="text-xs font-medium truncate"
							:class="{ 'italic text-muted-foreground': !user.supervisorName }"
						>
							{{ user.supervisorName || 'Sin supervisor' }}
						</p>
					</div>
				</div>

				<div class="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
					<div class="flex flex-wrap gap-1">
						<Badge
							v-for="role in user.roles?.slice(0, 2)"
							:key="role"
							variant="secondary"
							:class="['text-[10px] px-1.5 h-5', getRoleClasses(role).class]"
						>
							{{ getRoleClasses(role).label || role }}
						</Badge>
						<span
							v-if="(user.roles?.length ?? 0) > 2"
							class="text-[10px] text-muted-foreground font-medium self-center"
						>
							+{{ user.roles!.length - 2 }}
						</span>
					</div>

					<DropdownMenu v-if="user.isOwned || user.deletedAt">
						<DropdownMenuTrigger as-child>
							<Button
								variant="outline"
								size="sm"
								class="h-7 gap-1 text-xs"
							>
								Acciones <MoreVertical class="h-3 w-3" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							class="w-44"
						>
							<!-- Mismo gate que en la tabla de escritorio: un usuario dado de
							     baja no tiene pantalla de edición y la ruta responde 404. El
							     permiso también se revisa aquí; antes esta vista lo omitía. -->
							<DropdownMenuItem
								v-if="
									!user.deletedAt &&
									hasPermissionUser(
										PermissionUser?.UpdateUsersCompanies ?? '',
										EntityType?.CompanyType ?? ''
									)
								"
								@click="
									router.push({
										name: props.toEditUser?.name || 'users.edit',
										params: { id: user.id },
									})
								"
							>
								<Pencil class="w-3.5 h-3.5 mr-2" /> Editar
							</DropdownMenuItem>
							<DropdownMenuItem
								v-if="!user.deletedAt"
								@click="router.push({ name: 'users.permissions', params: { id: user.id } })"
							>
								<ShieldCheck class="w-3.5 h-3.5 mr-2" /> Permisos
							</DropdownMenuItem>
							<DropdownMenuItem
								v-if="!user.deletedAt && user.id !== userLogged?.id"
								class="text-destructive focus:text-destructive"
								@click="handleDeactivateUser(user.id.toString())"
							>
								<UserX class="w-3.5 h-3.5 mr-2" /> Desactivar
							</DropdownMenuItem>
							<DropdownMenuItem
								v-if="user.deletedAt"
								class="text-emerald-600 focus:text-emerald-600"
								@click="handleReactiveUser(user.id.toString())"
							>
								<UserCheck class="w-3.5 h-3.5 mr-2" /> Activar
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
		</div>

		<!-- Pagination -->
		<div
			v-if="pagination?.meta?.lastPage >= 1"
			class="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t"
		>
			<div class="flex items-center gap-3 order-2 sm:order-1">
				<p class="text-sm text-muted-foreground">
					Pág. <strong>{{ pagination.meta.currentPage }}</strong> /
					{{ pagination.meta.lastPage }}
				</p>
				<div class="flex items-center gap-2">
					<Label class="text-xs text-muted-foreground hidden sm:block"
						>Filas:</Label
					>
					<Select
						:model-value="filters.perPage?.toString()"
						@update:model-value="updatePerPage"
					>
						<SelectTrigger class="h-8 w-[64px] text-xs">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								<SelectItem
									v-for="option in PER_PAGE_OPTIONS"
									:key="option"
									:value="option"
									>{{ option }}</SelectItem
								>
							</SelectGroup>
						</SelectContent>
					</Select>
				</div>
			</div>

			<Pagination
				:total="pagination.meta.total"
				:sibling-count="1"
				:show-edges="true"
				:default-page="pagination.meta.currentPage"
				:items-per-page="pagination.meta.perPage"
				@update:page="changePage"
				class="order-1 sm:order-2 m-0 w-auto"
			>
				<PaginationContent v-slot="{ items }">
					<PaginationFirst />
					<PaginationPrevious />
					<template v-for="(item, index) in items">
						<PaginationItem
							v-if="item.type === 'page'"
							:key="index"
							:value="item.value"
							as-child
						>
							<Button
								class="w-9 h-9 p-0 text-xs"
								:variant="
									item.value === pagination.meta.currentPage ?
										'default'
									:	'ghost'
								"
							>
								{{ item.value }}
							</Button>
						</PaginationItem>
						<PaginationEllipsis
							v-else
							:key="'e-' + index"
							:index="index"
						/>
					</template>
					<PaginationNext />
					<PaginationLast />
				</PaginationContent>
			</Pagination>
		</div>
	</div>
</template>
